import React from 'react';
import { Open } from '@drift-labs/icons';
import useWalletIsConnected from '../../hooks/useWalletIsConnected';
import useGoToRoute from 'src/hooks/useGoToRoute';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import { PosthogEvent } from 'src/providers/posthog/posthogConfig';
import { Properties } from 'posthog-js';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { BASE_NAVIGATION_OPTIONS } from 'src/components/Navigation/config/consts';
import { RouteNavigationResult } from 'src/components/Navigation/types';
import { ProductType } from 'src/hooks/useEarnProduct';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from '../primitives/Button/Button';
import useIsMobile from 'src/hooks/useIsMobileScreenSize';

export type ActionButtonType =
	| 'deposit'
	| 'see_all'
	| 'details'
	| 'stake'
	| 'view_amplify'
	| 'super_stake'
	| 'stake_sol'
	| 'view_vaults';

interface EarnActionButtonProps {
	type: ActionButtonType;
	productType: ProductType;
	marketIndex?: number;
	marketSymbol?: string;
	highlight?: boolean;
	mobileText?: string;
	className?: string;
	size?: 'SMALL' | 'MEDIUM' | 'LARGE';
}

export const EarnActionButton: React.FC<EarnActionButtonProps> = ({
	type,
	marketIndex,
	marketSymbol,
	highlight = false,
	mobileText,
	className,
	productType,
}) => {
	const walletIsConnected = useWalletIsConnected();
	const goToRoute = useGoToRoute();
	const setDrawerStore = useDrawersStore((s) => s.set);
	const spotMarketLookup = useSpotMarketsLookup();
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const { captureEvent } = usePostHogCapture();
	const { show } = useModal();
	const isMobile = useIsMobile();

	const analyticsCtaWrapper = (
		cta: () => void,
		eventName: PosthogEvent,
		properties?: Properties
	) => {
		captureEvent(eventName, properties);
		cta();
	};

	const getButtonConfig = () => {
		switch (type) {
			case 'deposit':
				return {
					text: 'Deposit',
					requiresWallet: true,
					onClick: () => {
						if (marketIndex !== undefined) {
							show({
								type: 'deposit',
								props: {
									collateralIndex: marketIndex,
								},
							});
							captureEvent('opened_deposit_modal', {
								cta: 'drift_earn_deposit',
								from: 'drift_earn',
							});
						}
					},
				};

			case 'see_all':
				return {
					text: 'See All',
					requiresWallet: false,
					onClick: () => {
						if (productType === 'borrow_lend') {
							goToRoute('/earn/borrow-lend');
						}
						if (productType === 'insurance_fund_vaults') {
							goToRoute('/vaults/insurance-fund-vaults');
						}
					},
				};

			case 'details':
				return {
					text: <div>Details</div>,
					requiresWallet: false,
					onClick: () => {
						if (marketIndex !== undefined) {
							setDrawerStore((s) => {
								s.borrowLendDetails = {
									show: true,
									market: spotMarketLookup[marketIndex],
									displayTab: 'deposit',
								};
							});
						}
					},
				};

			case 'stake':
				return {
					text: marketSymbol ? `Stake ${marketSymbol}` : 'Stake',
					mobileText: 'Stake',
					requiresWallet: true,
					onClick: () => {
						if (marketIndex !== undefined) {
							analyticsCtaWrapper(
								() => {
									show({
										type: 'stake',
										props: {
											isDriftStaking: productType === 'governance',
											selectedIfStakeMarket:
												spotMarketsSortedByMarketIndex[marketIndex],
										},
									});
								},
								'clicked_drift_earn_if_stake',
								{ spotMarket: marketSymbol }
							);
						}
					},
				};

			case 'view_amplify':
				return {
					text: 'View Amplify',
					requiresWallet: false,
					onClick: () => {
						analyticsCtaWrapper(
							() =>
								goToRoute(
									(
										BASE_NAVIGATION_OPTIONS.amplify.handler() as RouteNavigationResult
									).route
								),
							'clicked_drift_earn_amplify',
							{ spotMarket: marketSymbol }
						);
					},
				};

			case 'super_stake':
				return {
					text: 'Super Stake',
					requiresWallet: false,
					linkOut: true,
					onClick: () => goToRoute('https://superstakesol.com'),
				};

			case 'stake_sol':
				return {
					text: 'Stake SOL',
					requiresWallet: false,
					onClick: () => {
						analyticsCtaWrapper(
							() =>
								goToRoute(
									(
										BASE_NAVIGATION_OPTIONS.dSolStaking.handler() as RouteNavigationResult
									).route
								),
							'clicked_drift_earn_dsol_staking',
							{ spotMarket: marketSymbol }
						);
					},
				};

			case 'view_vaults':
				return {
					text: 'View Vaults',
					requiresWallet: false,
					onClick: () => {
						analyticsCtaWrapper(
							() =>
								goToRoute(
									(
										BASE_NAVIGATION_OPTIONS.vaults.handler() as RouteNavigationResult
									).route
								),
							'clicked_drift_earn_vaults',
							{ spotMarket: marketSymbol }
						);
					},
				};

			default:
				return {
					text: 'Unknown',
					requiresWallet: false,
					onClick: () => console.warn('Unknown button type:', type),
				};
		}
	};

	const handleShowConnectWalletOptions = () => {
		show({ type: 'connect_wallet', props: {} });
	};

	const config = getButtonConfig();
	const showConnectButton = !walletIsConnected && config.requiresWallet;
	const displayText = mobileText || config.mobileText || config.text;

	if (showConnectButton) {
		return (
			<Button
				variant={'primary'}
				size={isMobile ? 'sm' : 'lg'}
				className={className}
				onClick={handleShowConnectWalletOptions}
			>
				Connect
			</Button>
		);
	}

	return (
		<Button
			variant={highlight ? 'primary' : 'secondary'}
			size={isMobile ? 'sm' : 'lg'}
			className={className}
			onClick={config.onClick}
			rightIcon={config.linkOut ? Open : undefined}
			dataTestId={`earn-action-${type}`}
		>
			{displayText}
		</Button>
	);
};
